import { supabase } from '../lib/supabase'
import type { 
  Document, 
  CreateDocumentData, 
  DocumentFilters, 
  DocumentStats, 
  DocumentPlanLimits,
  BusinessDocumentTemplate,
  DocumentTemplateLibraryMap,
  DocumentType
} from '../types/document'

// Built-in professional document templates
const DOCUMENT_TEMPLATES: DocumentTemplateLibraryMap = {
  'subcontractor_agreement': {
    name: 'Subcontractor Agreement',
    description: 'Standard agreement for hiring independent contractors and freelancers',
    category: 'Contracts',
    variables: [
      { key: 'CONTRACTOR_NAME', label: 'Contractor Name', type: 'text', required: true },
      { key: 'CONTRACTOR_ADDRESS', label: 'Contractor Address', type: 'text', required: true },
      { key: 'COMPANY_NAME', label: 'Company Name', type: 'text', required: true },
      { key: 'PROJECT_DESCRIPTION', label: 'Project Description', type: 'text', required: true },
      { key: 'START_DATE', label: 'Start Date', type: 'date', required: true },
      { key: 'END_DATE', label: 'End Date', type: 'date', required: false },
      { key: 'PAYMENT_AMOUNT', label: 'Payment Amount', type: 'currency', required: true },
      { key: 'PAYMENT_SCHEDULE', label: 'Payment Schedule', type: 'text', required: true }
    ],
    content: `
<h1>SUBCONTRACTOR AGREEMENT</h1>

<p>This Subcontractor Agreement ("Agreement") is entered into on {START_DATE} between {COMPANY_NAME} ("Company") and {CONTRACTOR_NAME} ("Contractor").</p>

<h2>1. SCOPE OF WORK</h2>
<p>Contractor agrees to provide the following services: {PROJECT_DESCRIPTION}</p>

<h2>2. COMPENSATION</h2>
<p>Company agrees to pay Contractor {PAYMENT_AMOUNT} according to the following schedule: {PAYMENT_SCHEDULE}</p>

<h2>3. INDEPENDENT CONTRACTOR STATUS</h2>
<p>Contractor acknowledges that they are an independent contractor and not an employee of Company. Contractor is responsible for their own taxes, insurance, and benefits.</p>

<h2>4. INTELLECTUAL PROPERTY</h2>
<p>All work product created by Contractor under this Agreement shall be considered "work for hire" and shall be the sole property of Company.</p>

<h2>5. CONFIDENTIALITY</h2>
<p>Contractor agrees to maintain strict confidentiality regarding all Company information and client data.</p>

<h2>6. TERMINATION</h2>
<p>Either party may terminate this Agreement with 30 days written notice.</p>

<h2>7. GOVERNING LAW</h2>
<p>This Agreement shall be governed by the laws of {GOVERNING_LAW}.</p>

<p>By signing below, both parties agree to the terms of this Agreement.</p>

<div class="signature-section">
<p>Company Representative: ___________________________ Date: ___________</p>
<p>Contractor: ___________________________ Date: ___________</p>
</div>
    `
  },
  'nda': {
    name: 'Non-Disclosure Agreement',
    description: 'Mutual or unilateral NDA to protect confidential information',
    category: 'Legal',
    variables: [
      { key: 'DISCLOSING_PARTY', label: 'Disclosing Party', type: 'text', required: true },
      { key: 'RECEIVING_PARTY', label: 'Receiving Party', type: 'text', required: true },
      { key: 'PURPOSE', label: 'Purpose of Disclosure', type: 'text', required: true },
      { key: 'EFFECTIVE_DATE', label: 'Effective Date', type: 'date', required: true },
      { key: 'EXPIRATION_DATE', label: 'Expiration Date', type: 'date', required: false },
      { key: 'GOVERNING_LAW', label: 'Governing Law', type: 'text', required: true }
    ],
    content: `
<h1>NON-DISCLOSURE AGREEMENT</h1>

<p>This Non-Disclosure Agreement ("Agreement") is entered into on {EFFECTIVE_DATE} between {DISCLOSING_PARTY} ("Disclosing Party") and {RECEIVING_PARTY} ("Receiving Party").</p>

<h2>1. PURPOSE</h2>
<p>The purpose of this Agreement is to protect confidential information disclosed in connection with: {PURPOSE}</p>

<h2>2. DEFINITION OF CONFIDENTIAL INFORMATION</h2>
<p>Confidential Information includes all non-public information, including but not limited to business plans, client lists, financial information, trade secrets, and any other proprietary information.</p>

<h2>3. OBLIGATIONS</h2>
<p>Receiving Party agrees to:</p>
<ul>
<li>Hold all Confidential Information in strict confidence</li>
<li>Not disclose Confidential Information to any third parties</li>
<li>Use Confidential Information solely for the stated purpose</li>
<li>Return or destroy all Confidential Information upon request</li>
</ul>

<h2>4. TERM</h2>
<p>This Agreement shall remain in effect until {EXPIRATION_DATE} or until terminated by either party with written notice.</p>

<h2>5. REMEDIES</h2>
<p>Receiving Party acknowledges that breach of this Agreement may cause irreparable harm and agrees that Disclosing Party may seek injunctive relief.</p>

<h2>6. GOVERNING LAW</h2>
<p>This Agreement shall be governed by the laws of {GOVERNING_LAW}.</p>

<div class="signature-section">
<p>Disclosing Party: ___________________________ Date: ___________</p>
<p>Receiving Party: ___________________________ Date: ___________</p>
</div>
    `
  },
  'model_release': {
    name: 'Model Release Form',
    description: 'Standard model release for photography and video work',
    category: 'Releases',
    variables: [
      { key: 'MODEL_NAME', label: 'Model Name', type: 'text', required: true },
      { key: 'PHOTOGRAPHER_NAME', label: 'Photographer Name', type: 'text', required: true },
      { key: 'SHOOT_DATE', label: 'Shoot Date', type: 'date', required: true },
      { key: 'SHOOT_LOCATION', label: 'Shoot Location', type: 'text', required: true },
      { key: 'COMPENSATION', label: 'Compensation', type: 'text', required: false }
    ],
    content: `
<h1>MODEL RELEASE FORM</h1>

<p>I, {MODEL_NAME} ("Model"), grant permission to {PHOTOGRAPHER_NAME} ("Photographer") to use my likeness in photographs and/or video taken on {SHOOT_DATE} at {SHOOT_LOCATION}.</p>

<h2>GRANT OF RIGHTS</h2>
<p>I hereby grant to Photographer and their assigns, licensees, and successors the irrevocable right and permission to:</p>
<ul>
<li>Use, publish, and distribute the photographs/video</li>
<li>Alter, crop, or manipulate the images</li>
<li>Use my name and likeness for commercial and non-commercial purposes</li>
<li>Display the images in any media format</li>
</ul>

<h2>WAIVER</h2>
<p>I waive any right to inspect or approve the finished images or any printed or electronic matter that may be used in conjunction with them.</p>

<h2>COMPENSATION</h2>
<p>Compensation for this release: {COMPENSATION}</p>

<h2>LEGAL</h2>
<p>I am of legal age and have the authority to enter into this agreement. I understand this release is binding upon my heirs and assigns.</p>

<div class="signature-section">
<p>Model Signature: ___________________________ Date: ___________</p>
<p>Print Name: {MODEL_NAME}</p>
<p>Photographer: ___________________________ Date: ___________</p>
</div>
    `
  },
  'client_contract': {
    name: 'Photography Client Contract',
    description: 'Comprehensive contract for photography services',
    category: 'Contracts',
    variables: [
      { key: 'CLIENT_NAME', label: 'Client Name', type: 'text', required: true },
      { key: 'CLIENT_ADDRESS', label: 'Client Address', type: 'text', required: true },
      { key: 'PHOTOGRAPHER_NAME', label: 'Photographer Name', type: 'text', required: true },
      { key: 'EVENT_TYPE', label: 'Event Type', type: 'text', required: true },
      { key: 'EVENT_DATE', label: 'Event Date', type: 'date', required: true },
      { key: 'EVENT_LOCATION', label: 'Event Location', type: 'text', required: true },
      { key: 'TOTAL_COST', label: 'Total Cost', type: 'currency', required: true },
      { key: 'DEPOSIT_AMOUNT', label: 'Deposit Amount', type: 'currency', required: true }
    ],
    content: `
<h1>PHOTOGRAPHY SERVICES CONTRACT</h1>

<p>This Contract is between {PHOTOGRAPHER_NAME} ("Photographer") and {CLIENT_NAME} ("Client") for photography services.</p>

<h2>1. EVENT DETAILS</h2>
<p>Event: {EVENT_TYPE}</p>
<p>Date: {EVENT_DATE}</p>
<p>Location: {EVENT_LOCATION}</p>

<h2>2. SERVICES PROVIDED</h2>
<p>Photographer will provide professional photography services including image capture, basic editing, and delivery of final images.</p>

<h2>3. PAYMENT TERMS</h2>
<p>Total Cost: {TOTAL_COST}</p>
<p>Deposit: {DEPOSIT_AMOUNT} (due upon signing)</p>
<p>Balance: Due 30 days before event date</p>

<h2>4. DELIVERY</h2>
<p>Final edited images will be delivered within 4-6 weeks after the event via online gallery.</p>

<h2>5. CANCELLATION POLICY</h2>
<p>Cancellations more than 60 days before event: Full refund minus $200 processing fee</p>
<p>Cancellations 30-60 days: 50% refund</p>
<p>Cancellations less than 30 days: No refund</p>

<h2>6. COPYRIGHT</h2>
<p>Photographer retains copyright to all images. Client receives license for personal use.</p>

<div class="signature-section">
<p>Photographer: ___________________________ Date: ___________</p>
<p>Client: ___________________________ Date: ___________</p>
</div>
    `
  },
  'location_release': {
    name: 'Location Release Form',
    description: 'Release form for filming/photography on private property',
    category: 'Releases',
    variables: [
      { key: 'PROPERTY_OWNER', label: 'Property Owner', type: 'text', required: true },
      { key: 'PHOTOGRAPHER_NAME', label: 'Photographer Name', type: 'text', required: true },
      { key: 'PROPERTY_ADDRESS', label: 'Property Address', type: 'text', required: true },
      { key: 'SHOOT_DATE', label: 'Shoot Date', type: 'date', required: true },
      { key: 'COMPENSATION', label: 'Compensation', type: 'text', required: false }
    ],
    content: `
<h1>LOCATION RELEASE FORM</h1>

<p>I, {PROPERTY_OWNER} ("Owner"), grant permission to {PHOTOGRAPHER_NAME} ("Photographer") to use my property located at {PROPERTY_ADDRESS} for photography/videography on {SHOOT_DATE}.</p>

<h2>GRANT OF RIGHTS</h2>
<p>I hereby grant to Photographer the right to:</p>
<ul>
<li>Photograph/film on the specified property</li>
<li>Use, publish, and distribute images of the property</li>
<li>Use the images for commercial and non-commercial purposes</li>
<li>Display the images in any media format</li>
</ul>

<h2>COMPENSATION</h2>
<p>Compensation for this release: {COMPENSATION}</p>

<h2>LIABILITY</h2>
<p>Photographer agrees to maintain appropriate insurance and conduct activities safely.</p>

<div class="signature-section">
<p>Property Owner: ___________________________ Date: ___________</p>
<p>Print Name: {PROPERTY_OWNER}</p>
<p>Photographer: ___________________________ Date: ___________</p>
</div>
    `
  },
  'vendor_agreement': {
    name: 'Vendor Agreement',
    description: 'Standard vendor/supplier agreement',
    category: 'Contracts',
    variables: [
      { key: 'VENDOR_NAME', label: 'Vendor Name', type: 'text', required: true },
      { key: 'COMPANY_NAME', label: 'Company Name', type: 'text', required: true },
      { key: 'SERVICES_DESCRIPTION', label: 'Services Description', type: 'text', required: true },
      { key: 'PAYMENT_TERMS', label: 'Payment Terms', type: 'text', required: true }
    ],
    content: `
<h1>VENDOR AGREEMENT</h1>

<p>This Agreement is between {COMPANY_NAME} ("Company") and {VENDOR_NAME} ("Vendor").</p>

<h2>1. SERVICES</h2>
<p>Vendor will provide: {SERVICES_DESCRIPTION}</p>

<h2>2. PAYMENT</h2>
<p>Payment Terms: {PAYMENT_TERMS}</p>

<h2>3. QUALITY STANDARDS</h2>
<p>All services must meet professional industry standards.</p>

<div class="signature-section">
<p>Company: ___________________________ Date: ___________</p>
<p>Vendor: ___________________________ Date: ___________</p>
</div>
    `
  },
  'employment_contract': {
    name: 'Employment Contract',
    description: 'Standard employment agreement',
    category: 'Employment',
    variables: [
      { key: 'EMPLOYEE_NAME', label: 'Employee Name', type: 'text', required: true },
      { key: 'COMPANY_NAME', label: 'Company Name', type: 'text', required: true },
      { key: 'POSITION', label: 'Position', type: 'text', required: true },
      { key: 'SALARY', label: 'Salary', type: 'currency', required: true },
      { key: 'START_DATE', label: 'Start Date', type: 'date', required: true }
    ],
    content: `
<h1>EMPLOYMENT CONTRACT</h1>

<p>This Employment Contract is between {COMPANY_NAME} ("Company") and {EMPLOYEE_NAME} ("Employee").</p>

<h2>1. POSITION</h2>
<p>Position: {POSITION}</p>
<p>Start Date: {START_DATE}</p>

<h2>2. COMPENSATION</h2>
<p>Annual Salary: {SALARY}</p>

<h2>3. BENEFITS</h2>
<p>Employee is entitled to company benefits as outlined in the employee handbook.</p>

<div class="signature-section">
<p>Company: ___________________________ Date: ___________</p>
<p>Employee: ___________________________ Date: ___________</p>
</div>
    `
  },
  'service_agreement': {
    name: 'Service Agreement',
    description: 'General service agreement template',
    category: 'Contracts',
    variables: [
      { key: 'SERVICE_PROVIDER', label: 'Service Provider', type: 'text', required: true },
      { key: 'CLIENT_NAME', label: 'Client Name', type: 'text', required: true },
      { key: 'SERVICE_DESCRIPTION', label: 'Service Description', type: 'text', required: true }
    ],
    content: `<h1>SERVICE AGREEMENT</h1><p>Agreement between {SERVICE_PROVIDER} and {CLIENT_NAME} for {SERVICE_DESCRIPTION}.</p>`
  },
  'licensing_agreement': {
    name: 'Licensing Agreement',
    description: 'License agreement for intellectual property',
    category: 'Legal',
    variables: [
      { key: 'LICENSOR', label: 'Licensor', type: 'text', required: true },
      { key: 'LICENSEE', label: 'Licensee', type: 'text', required: true }
    ],
    content: `<h1>LICENSING AGREEMENT</h1><p>License granted from {LICENSOR} to {LICENSEE}.</p>`
  },
  'partnership_agreement': {
    name: 'Partnership Agreement',
    description: 'Business partnership agreement',
    category: 'Legal',
    variables: [
      { key: 'PARTNER1', label: 'Partner 1', type: 'text', required: true },
      { key: 'PARTNER2', label: 'Partner 2', type: 'text', required: true }
    ],
    content: `<h1>PARTNERSHIP AGREEMENT</h1><p>Partnership between {PARTNER1} and {PARTNER2}.</p>`
  },
  'independent_contractor': {
    name: 'Independent Contractor Agreement',
    description: 'Agreement for independent contractor services',
    category: 'Contracts',
    variables: [
      { key: 'CONTRACTOR_NAME', label: 'Contractor Name', type: 'text', required: true },
      { key: 'CLIENT_NAME', label: 'Client Name', type: 'text', required: true }
    ],
    content: `<h1>INDEPENDENT CONTRACTOR AGREEMENT</h1><p>Agreement between {CLIENT_NAME} and {CONTRACTOR_NAME}.</p>`
  },
  'work_for_hire': {
    name: 'Work for Hire Agreement',
    description: 'Work for hire copyright agreement',
    category: 'Legal',
    variables: [
      { key: 'CONTRACTOR_NAME', label: 'Contractor Name', type: 'text', required: true },
      { key: 'CLIENT_NAME', label: 'Client Name', type: 'text', required: true }
    ],
    content: `<h1>WORK FOR HIRE AGREEMENT</h1><p>Work for hire agreement between {CLIENT_NAME} and {CONTRACTOR_NAME}.</p>`
  },
  'privacy_policy': {
    name: 'Privacy Policy',
    description: 'Standard privacy policy template',
    category: 'Legal',
    variables: [
      { key: 'COMPANY_NAME', label: 'Company Name', type: 'text', required: true }
    ],
    content: `<h1>PRIVACY POLICY</h1><p>Privacy policy for {COMPANY_NAME}.</p>`
  },
  'terms_of_service': {
    name: 'Terms of Service',
    description: 'Standard terms of service template',
    category: 'Legal',
    variables: [
      { key: 'COMPANY_NAME', label: 'Company Name', type: 'text', required: true }
    ],
    content: `<h1>TERMS OF SERVICE</h1><p>Terms of service for {COMPANY_NAME}.</p>`
  },
  'custom': {
    name: 'Custom Document',
    description: 'Blank template for custom documents',
    category: 'Custom',
    variables: [],
    content: `<h1>CUSTOM DOCUMENT</h1><p>Your custom content here.</p>`
  }
} as const

class DocumentService {
  async getDocuments(userId: string, filters: DocumentFilters = {}): Promise<{
    success: boolean
    documents?: Document[]
    error?: string
  }> {
    try {
      if (!supabase) {
        throw new Error('Supabase client not initialized')
      }

      let query = supabase
        .from('documents')
        .select('*, parties(*)')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })

      // Apply filters
      if (filters.documentType?.length) {
        query = query.in('document_type', filters.documentType)
      }
      if (filters.status?.length) {
        query = query.in('status', filters.status)
      }
      if (filters.category) {
        query = query.eq('category', filters.category)
      }
      if (filters.search) {
        query = query.or(`title.ilike.%${filters.search}%,description.ilike.%${filters.search}%`)
      }

      const { data, error } = await query

      if (error) throw error

      return {
        success: true,
        documents: data || []
      }
    } catch (error) {
      console.error('Error fetching documents:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch documents'
      }
    }
  }

  async createDocument(userId: string, documentData: CreateDocumentData): Promise<{
    success: boolean
    document?: Document
    error?: string
  }> {
    try {
      if (!supabase) {
        throw new Error('Supabase client not initialized')
      }

      // Generate document content from template if templateId provided
      let content = ''
      if (documentData.templateId && DOCUMENT_TEMPLATES[documentData.documentType as keyof typeof DOCUMENT_TEMPLATES]) {
        const template = DOCUMENT_TEMPLATES[documentData.documentType as keyof typeof DOCUMENT_TEMPLATES]
        content = this.fillTemplate(template.content, documentData.variables)
      }

      const document = {
        user_id: userId,
        title: documentData.title,
        document_type: documentData.documentType,
        status: 'draft' as const,
        template_id: documentData.templateId,
        content,
        variables: documentData.variables,
        description: documentData.description,
        tags: documentData.tags,
        category: documentData.category,
        effective_date: documentData.effectiveDate,
        expiration_date: documentData.expirationDate,
        contract_value: documentData.contractValue,
        payment_terms: documentData.paymentTerms,
        cancellation_terms: documentData.cancellationTerms,
        governing_law: documentData.governingLaw,
        jurisdiction: documentData.jurisdiction,
        signature_required: documentData.signatureRequired,
        version: 1,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }

      const { data, error } = await supabase
        .from('documents')
        .insert(document)
        .select('*')
        .single()

      if (error) throw error

      // Create parties
      if (documentData.parties.length > 0) {
        const parties = documentData.parties.map(party => ({
          ...party,
          document_id: data.id,
          signed: false
        }))

        await supabase
          .from('document_parties')
          .insert(parties)
      }

      return {
        success: true,
        document: data
      }
    } catch (error) {
      console.error('Error creating document:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create document'
      }
    }
  }

  async getDocumentTemplates(): Promise<{
    success: boolean
    templates?: BusinessDocumentTemplate[]
    error?: string
  }> {
    try {
      // Return built-in templates converted to BusinessDocumentTemplate format
      const templates: BusinessDocumentTemplate[] = Object.entries(DOCUMENT_TEMPLATES).map(([type, template]) => ({
        id: type,
        userId: 'system',
        name: template.name,
        description: template.description,
        documentType: type as DocumentType,
        category: template.category,
        content: template.content,
        variables: template.variables,
        isPublic: true,
        isDefault: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }))

      return {
        success: true,
        templates
      }
    } catch (error) {
      console.error('Error fetching document templates:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch templates'
      }
    }
  }

  async getDocumentStats(userId: string): Promise<{
    success: boolean
    stats?: DocumentStats
    error?: string
  }> {
    try {
      if (!supabase) {
        throw new Error('Supabase client not initialized')
      }

      const { data, error } = await supabase
        .from('documents')
        .select('status, document_type, expiration_date')
        .eq('user_id', userId)

      if (error) throw error

      const now = new Date()
      const thisMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0)

      const stats: DocumentStats = {
        totalDocuments: data.length,
        activeContracts: data.filter(d => d.status === 'active').length,
        pendingReview: data.filter(d => d.status === 'pending_review').length,
        expiringThisMonth: data.filter(d => 
          d.expiration_date && new Date(d.expiration_date) <= thisMonth
        ).length,
        signaturesPending: data.filter(d => d.status === 'draft').length,
        documentsByType: data.reduce((acc, doc) => {
          acc[doc.document_type as DocumentType] = (acc[doc.document_type as DocumentType] || 0) + 1
          return acc
        }, {} as Record<DocumentType, number>),
        documentsByStatus: data.reduce((acc, doc) => {
          acc[doc.status as Document['status']] = (acc[doc.status as Document['status']] || 0) + 1
          return acc
        }, {} as Record<Document['status'], number>)
      }

      return {
        success: true,
        stats
      }
    } catch (error) {
      console.error('Error fetching document stats:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch stats'
      }
    }
  }

  private fillTemplate(template: string, variables: Record<string, string>): string {
    let content = template
    Object.entries(variables).forEach(([key, value]) => {
      const regex = new RegExp(`{${key}}`, 'g')
      content = content.replace(regex, value)
    })
    return content
  }

  async getPlanLimits(subscriptionStatus: string, userId?: string): Promise<DocumentPlanLimits> {
    const isPremium = subscriptionStatus === 'professional' || subscriptionStatus === 'studio'
    
    // Calculate current month usage
    let currentMonthUsage = 0
    try {
      if (!supabase || !userId) {
        throw new Error('Supabase client not initialized or userId not provided')
      }

      const now = new Date()
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString()
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59).toISOString()

      const { data, error } = await supabase
        .from('documents')
        .select('id')
        .eq('user_id', userId)
        .gte('created_at', startOfMonth)
        .lte('created_at', endOfMonth)

      if (!error && data) {
        currentMonthUsage = data.length
      }
    } catch (error) {
      console.error('Error calculating current month usage:', error)
    }
    
    return {
      maxDocumentsPerMonth: isPremium ? -1 : 10,
      hasUnlimitedDocuments: isPremium,
      currentMonthUsage,
      canCreateDocument: isPremium || currentMonthUsage < 10,
      maxTemplates: isPremium ? -1 : 5,
      hasUnlimitedTemplates: isPremium,
      currentTemplatesCount: 0,
      canCreateTemplate: isPremium,
      hasAdvancedFeatures: isPremium
    }
  }

  async deleteDocument(documentId: string, userId: string): Promise<{
    success: boolean
    error?: string
  }> {
    try {
      if (!supabase) {
        throw new Error('Supabase client not initialized')
      }

      // First delete related parties
      await supabase
        .from('document_parties')
        .delete()
        .eq('document_id', documentId)

      // Then delete the document (with user verification for security)
      const { error } = await supabase
        .from('documents')
        .delete()
        .eq('id', documentId)
        .eq('user_id', userId)

      if (error) throw error

      return { success: true }
    } catch (error) {
      console.error('Error deleting document:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to delete document'
      }
    }
  }

  async getDocument(documentId: string, userId: string): Promise<{
    success: boolean
    document?: Document
    error?: string
  }> {
    try {
      if (!supabase) {
        throw new Error('Supabase client not initialized')
      }

      const { data, error } = await supabase
        .from('documents')
        .select('*, parties(*)')
        .eq('id', documentId)
        .eq('user_id', userId)
        .single()

      if (error) throw error

      return {
        success: true,
        document: data
      }
    } catch (error) {
      console.error('Error fetching document:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch document'
      }
    }
  }

  async updateDocument(documentId: string, userId: string, updateData: Partial<CreateDocumentData>): Promise<{
    success: boolean
    document?: Document
    error?: string
  }> {
    try {
      if (!supabase) {
        throw new Error('Supabase client not initialized')
      }

      // Update document content if template variables changed
      let content = undefined
      if (updateData.variables && updateData.templateId) {
        const template = DOCUMENT_TEMPLATES[updateData.documentType as keyof typeof DOCUMENT_TEMPLATES]
        if (template) {
          content = this.fillTemplate(template.content, updateData.variables)
        }
      }

      const updateFields = {
        title: updateData.title,
        description: updateData.description,
        tags: updateData.tags,
        category: updateData.category,
        effective_date: updateData.effectiveDate,
        expiration_date: updateData.expirationDate,
        contract_value: updateData.contractValue,
        payment_terms: updateData.paymentTerms,
        cancellation_terms: updateData.cancellationTerms,
        governing_law: updateData.governingLaw,
        jurisdiction: updateData.jurisdiction,
        signature_required: updateData.signatureRequired,
        variables: updateData.variables,
        content,
        updated_at: new Date().toISOString()
      }

      // Remove undefined values
      Object.keys(updateFields).forEach(key => {
        if (updateFields[key as keyof typeof updateFields] === undefined) {
          delete updateFields[key as keyof typeof updateFields]
        }
      })

      const { data, error } = await supabase
        .from('documents')
        .update(updateFields)
        .eq('id', documentId)
        .eq('user_id', userId)
        .select('*')
        .single()

      if (error) throw error

      // Update parties if provided
      if (updateData.parties) {
        // Delete existing parties
        await supabase
          .from('document_parties')
          .delete()
          .eq('document_id', documentId)

        // Insert new parties
        if (updateData.parties.length > 0) {
          const parties = updateData.parties.map(party => ({
            ...party,
            document_id: documentId,
            signed: false
          }))

          await supabase
            .from('document_parties')
            .insert(parties)
        }
      }

      return {
        success: true,
        document: data
      }
    } catch (error) {
      console.error('Error updating document:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update document'
      }
    }
  }

  // Generate PDF content (basic HTML to PDF conversion)
  generatePDFContent(document: Document): string {
    return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>${document.title}</title>
    <style>
        body { 
            font-family: Arial, sans-serif; 
            line-height: 1.6; 
            max-width: 800px; 
            margin: 0 auto; 
            padding: 20px; 
        }
        h1 { color: #333; border-bottom: 2px solid #333; padding-bottom: 10px; }
        h2 { color: #555; margin-top: 30px; }
        .signature-section { 
            margin-top: 50px; 
            padding-top: 20px; 
            border-top: 1px solid #ccc; 
        }
        .document-header {
            text-align: center;
            margin-bottom: 30px;
            padding-bottom: 20px;
            border-bottom: 1px solid #eee;
        }
        .document-info {
            background: #f9f9f9;
            padding: 15px;
            margin-bottom: 20px;
            border-radius: 5px;
        }
    </style>
</head>
<body>
    <div class="document-header">
        <h1>${document.title}</h1>
        <p><strong>Document ID:</strong> ${document.id}</p>
        <p><strong>Created:</strong> ${new Date(document.createdAt).toLocaleDateString()}</p>
        ${document.effectiveDate ? `<p><strong>Effective Date:</strong> ${new Date(document.effectiveDate).toLocaleDateString()}</p>` : ''}
    </div>
    
    ${document.description ? `<div class="document-info"><strong>Description:</strong> ${document.description}</div>` : ''}
    
    <div class="document-content">
        ${document.content || 'No content available'}
    </div>
    
    ${document.parties.length > 0 ? `
    <div class="signature-section">
        <h2>Parties to this Agreement</h2>
        ${document.parties.map(party => `
            <div style="margin-bottom: 20px;">
                <p><strong>${party.name}</strong> (${party.role})</p>
                <p>${party.email}</p>
                ${party.company ? `<p>${party.company}</p>` : ''}
                <p>Signature: ___________________________ Date: ___________</p>
            </div>
        `).join('')}
    </div>
    ` : ''}
</body>
</html>
    `
  }
}

export const documentService = new DocumentService() 